import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Button, Box, CircularProgress, Grid, Link, Typography, Container } from '@aura/components'
import { withStyles } from '@aura/components/styles'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import colors from '../../../../../public/colors'
import SendChallengeStatus from '../../../../constants/send-challenge'
import RegistrationContainer from '../../../../components/new-registration-container'
import AccountRecoveryTypes from '../../../../constants/factor-types'
import constants from '../../../../constants/languages'
import { hiddenFormatPhoneNumber } from '../../../../utils/formatNumber'
import CustomLoader from '../../../../components/custom-loader'
import styles from './styles'


const SMS_CODE_LENGTH = 6
const CALL_CODE_LENGTH = 5

const VerificationCodeInput = ({
    value,
    onChange,
    length,
    classes,
    theme,
    translate,
    isVerified,
    verificationError
}) => {
   
    const [ isFocusedOnce, setIsFocusedOnce ] = useState(false)
    const refs = useRef([ ...Array(length) ].map(() => null))

    useEffect(() => {
        if (verificationError && value.length > 0) {
            const lastFilledIndex = value.length - 1
            setTimeout(() => {
                if (refs.current[lastFilledIndex]) {
                    refs.current[lastFilledIndex].focus()
                }
            }, 100)
        }
    }, [ verificationError ])

    const handleChange = (idx, e) => {
        const val = e.target.value.replace(/[^0-9]/g, '')
        let arr = value.split('')
        arr[idx] = val ? val[0] : ''
        let newVal = arr.join('')
        onChange(newVal.length > length ? newVal.slice(0, length) : newVal)
        val && idx < length - 1
            ? setTimeout(() => refs.current[idx + 1].focus(), 0)
            : null
        !isFocusedOnce && idx !== 0
            ? setIsFocusedOnce(true)
            : null
    }

    const handleKeyDown = (idx, e) => {
        e.key === 'Backspace' && !value[idx] && idx > 0
            ? refs.current[idx - 1].focus()
            : null
        !isFocusedOnce && idx !== 0
            ? setIsFocusedOnce(true)
            : null
    }

    return (
        <Box className={`${classes.verificationCodeBox} ${length === SMS_CODE_LENGTH ? classes.verificationContainerSMS : classes.verificationCodeBoxCall}`}>
            <Typography
                className={classes.verificationCodeLabel}
                style={{
                    color: isFocusedOnce && value.length === 0
                        ? colors[theme.palette.mode].errorText
                        : ''
                }}
            >
                {translate('registrationFlow.verificationCode')}
            </Typography>
            <Box className={classes.verificationCodeInputContainer}>
                {[ ...Array(length) ].map((_, idx) => (
                    <input
                        key={idx}
                        ref={el => refs.current[idx] = el}
                        type='text'
                        inputMode='numeric'
                        maxLength={1}
                        autoFocus={idx === 0}
                        value={value[idx] || ''}
                        onChange={e => handleChange(idx, e)}
                        onKeyDown={e => handleKeyDown(idx, e)}
                        disabled={isVerified || (idx > 0 ? !value[idx - 1] : false)}
                        className={classes.verificationCodeInput}
                        style={{
                            borderBottom: isVerified
                                ? `2px solid ${colors[theme.palette.mode].successTextLight}`
                                : verificationError ? `2px solid ${colors[theme.palette.mode].errorText}`
                                    : value[idx]
                                        ? `2px solid ${colors[theme.palette.mode].verificationCodeInputFilledBorder}`
                                        : `2px solid ${colors[theme.palette.mode].verificationCodeInputUnfilledBorder}`,
                            color: isVerified ? colors[theme.palette.mode].successText : '',
                            pointerEvents: isVerified ? 'none' : 'auto'
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

const Component = ({
    changeStep,
    classes,
    data,
    error,
    initSendChallenge,
    isProcessing,
    isVerified,
    onNext,
    onResendCode,
    resendSuccessMsg,
    sendChallengeStatus,
    sendChallengeError,
    selectedRecoveryType,
    verificationError,
}) => {
    const { phoneNumber, phoneExtension } = data
    const { theme } = useAuraThemeSwitcher()
    const [ successMsg, setSuccessMsg ] = useState('')
    const { t: translate, i18n } = useTranslation()
    const [ timer, setTimer ] = useState(30)
    const [ canResend, setCanResend ] = useState(false)
    const [ nextButtonDisabled, setNextButtonDisabled ] = useState(true)

    const [ selectedLanguage, setSelectedLanguage ] = useState(i18n.language)


    useEffect(() => {
        resendSuccessMsg && setSuccessMsg(translate('registrationFlow.resendSuccessMsg'))
        if(resendSuccessMsg){
            verificationError = ''
            setPassCode('')
        }
    }, [ resendSuccessMsg ])

    useEffect(() => {
        initSendChallenge({ recoveryType: selectedRecoveryType })
    }, [])

    useEffect(() => {
        if( isVerified ){
            setTimeout(() => {
                changeStep()
            }, 3000)
            
        }
    }, [ isVerified ])


    useEffect(() => {
        let countdown
        const startCountdown = () => {
            countdown = setTimeout(() => {
                setTimer(prevTimer => prevTimer - 1)

                if (timer <= 1) {
                    setCanResend(true)
                    setSuccessMsg('')
                }

                if (timer > 1) {
                    startCountdown()
                }
            }, 1000)
        }

        startCountdown()

        return () => {
            if (countdown) {
                clearTimeout(countdown)
            }
        }
    }, [ timer ])
    

    const [ passCode, setPassCode ] = useState('')

    useEffect(() => {
        const requiredLength = selectedRecoveryType === AccountRecoveryTypes.SMS
            ? SMS_CODE_LENGTH
            : selectedRecoveryType === AccountRecoveryTypes.CALL
                ? CALL_CODE_LENGTH
                : 0
        setNextButtonDisabled(passCode.length !== requiredLength)
    }, [ passCode, selectedRecoveryType ])

    const handleResendCode = useCallback(event => {
        event.preventDefault()
        if (canResend) {
            setSuccessMsg('')
            onResendCode({ recoveryType: selectedRecoveryType })
            setCanResend(false)
            setTimer(30)
        }
    }, [ canResend ])


    const isLoading = sendChallengeStatus === SendChallengeStatus.PENDING
    const requiredPassCodeLength =  selectedRecoveryType === AccountRecoveryTypes.SMS ? SMS_CODE_LENGTH : CALL_CODE_LENGTH
    const title = translate(selectedRecoveryType === AccountRecoveryTypes.SMS ? 'registrationFlow.smsVerificationCode' : 'registrationFlow.callVerificationCode')
    const verbiage = isLoading ? '' : translate(selectedRecoveryType === AccountRecoveryTypes.SMS ? 'registrationFlow.smsVerbiage' : 'registrationFlow.callVerbiage', { phoneExtension: phoneExtension ? ` x${phoneExtension}` : '',phoneNumber: hiddenFormatPhoneNumber(phoneNumber) })

    const handleSubmit = useCallback(
        values => {
            onNext({
                passCode: passCode,
                recoveryType: selectedRecoveryType
            })
        },
        [ onNext, selectedRecoveryType, passCode ]
    )
    
    const mainContent = useMemo(() => (
        <>{
            isLoading || isProcessing ? (
                <Container className={classes.customLoader}>
                    <CustomLoader
                        label={translate('loading')}
                    />
                </Container>
            ) :   <>
                <Box className={classes.content}>
                    {
                        <Typography className={`${classes.subTitle} ${classes.newLine}`}>{verbiage}</Typography>
                    }
                    {!isLoading && (
                        <Grid className={classes.verificationContainer}>
                            <VerificationCodeInput
                                value={passCode}
                                onChange={val => setPassCode(val)}
                                length={requiredPassCodeLength}
                                classes={classes}
                                theme={theme}
                                translate={translate}
                                isVerified={isVerified}
                                verificationError={verificationError}
                            />
                            {isVerified && (
                                <Grid className={classes.verificationSuccess}>
                                    <CheckCircleOutlineIcon className={classes.verificationSuccessIcon} />
                                    <Typography className={classes.verificationSuccessText}>
                                        {translate('registrationFlow.verificationSuccess')}
                                    </Typography>

                                </Grid>
                            )}
                            {!isVerified && (
                                <Box className={classes.verificationStatusContainer}>
                                    {verificationError ? (
                                        <Box className={classes.verificationError}>
                                            <ErrorOutlineOutlinedIcon className={classes.verificationErrorIcon} />
                                            <Typography className={classes.verificationErrorText}>
                                                { translate('registrationFlow.incorrectVerificationCode')}
                                            </Typography>
                                        </Box>)
                                        : error && (
                                            <Box className={classes.verificationError}>
                                                <ErrorOutlineOutlinedIcon className={classes.verificationErrorIcon} />
                                                <Typography className={classes.verificationErrorText}>
                                                    {translate('oktaApiResponse.' + `${error}`)}
                                                </Typography>
                                            </Box>
                                        )}
                                    <Grid className={`${classes.resendVerification} 
                            ${selectedRecoveryType === AccountRecoveryTypes.CALL && classes.resendVerificationCall} 
                            ${sendChallengeError && timer > 0 && classes.resendVerificationSendChallengeErr}`}
                                    >
                                        <Box>
                                            <Link
                                                disabled={isLoading}
                                                onClick={handleResendCode}
                                                underline='none'
                                                className={`${canResend ? classes.link : classes.disabledLink} 
                                        ${sendChallengeError && timer > 0 && selectedRecoveryType === AccountRecoveryTypes.SMS && selectedLanguage !== constants.FRENCH && classes.resendVerificationSendChallengeErrSmsEn} 
                                        ${sendChallengeError && timer > 0 && selectedRecoveryType === AccountRecoveryTypes.SMS && selectedLanguage === constants.FRENCH && classes.resendVerificationSendChallengeErrSmsFr} 
                                        ${sendChallengeError && timer > 0 && selectedRecoveryType === AccountRecoveryTypes.CALL && selectedLanguage !== constants.FRENCH && classes.resendVerificationSendChallengeErrCallEn} 
                                        ${sendChallengeError && timer > 0 && selectedRecoveryType === AccountRecoveryTypes.CALL && selectedLanguage === constants.FRENCH && classes.resendVerificationSendChallengeErrCallFr}`
                                                }
                                            >
                                                {canResend ? translate('registrationFlow.resendVerificationCode') : sendChallengeError && timer > 0 ? translate('wait', { timer: timer }) : translate('registrationFlow.enterYourCode')}
                                            </Link>
                                        </Box>
                                        {!isLoading && !canResend
                                            ? (
                                                <Box className={`${classes.countdown} ${sendChallengeError && classes.countdownErr}`}>
                                                    <CircularProgress
                                                        className={classes.progressBar}
                                                        variant='determinate'
                                                        value={(timer / 30) * 100}
                                                        size={25}
                                                        thickness={5}
                                                    />
                                                    <Typography className={classes.countdownTypography}>{timer}</Typography>
                                                </Box>
                                            )
                                            : (
                                                <Box className={classes.countdownEmpty}></Box>
                                            )}

                                          
                                    </Grid>
                                    {successMsg && (
                                        <div className={classes.resendverificationCode}>
                                            {translate('registrationFlow.resendSuccessMsg')}
                                        </div>
                                    )}
                                </Box>
                            )}

                        </Grid>
                    )}
                </Box>
                <div className={`${classes.helpText} ${isVerified ? classes.helpTextVerified : ''}`}>
                    <p className={classes.subTitle}>{translate('registrationFlow.helpText')}</p>
                </div>
            </>
        }
        </>
    ), [ isLoading, classes, verbiage, passCode, requiredPassCodeLength, theme, isVerified, verificationError, selectedRecoveryType, sendChallengeError, timer, handleResendCode, canResend, selectedLanguage, error, isProcessing ])
    
    const footerContent = useMemo(() => (
        <Grid className={classes.buttonContainer}>
            <Button
                className={`${classes.nextButton} ${nextButtonDisabled && classes.nextButtonDisabled}`}
                color='primary'
                disabled = { nextButtonDisabled }
                onClick={handleSubmit}
            >
                {translate('next')}
            </Button>
        </Grid>
    ), [ classes, nextButtonDisabled, handleSubmit, translate ])
    return (
        <RegistrationContainer {...{ footerActions : !isVerified && !isLoading && !isProcessing && footerContent, mainContent, title, verbiage }}>
            
        </RegistrationContainer>
    )
}

Component.displayName = 'Verification'

Component.defaultProps = {
    data: {
        phoneNumber: ''
    }
}

Component.propTypes = {
    classes: PropTypes.shape({
        button: PropTypes.object,
        loader: PropTypes.object,
        root: PropTypes.object
    }),
    data: PropTypes.object.isRequired,
    error: PropTypes.object,
    initSendChallenge: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onResendCode: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    resendSuccessMsg: PropTypes.string,
    sendChallengeStatus: PropTypes.string
}

export default withStyles(Component, styles)
